import { useMemo } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { Input, Checkbox } from "../../../../common/components/Input";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-zone-sort and content-zone-detect both store a spatial `hotspot_info`
// array. Hotspots are AUTHORED in VR (their placement — rotation_*/position_* —
// plus font_size/scale come from the Spatial Studio), so this editor never adds
// or removes rows: it only edits the translatable text and the small set of
// per-hotspot flags/order the form is allowed to touch. Every placement key and
// any key not listed below is passed through UNTOUCHED.
//
// variant "zone-sort" items:
//   { index, id_internal, explanation, font_size, scale,
//     rotation_x, rotation_y, rotation_z, position_x?, position_y?, position_z? }
//   • explanation — translatable text (editable in every language)
//   • index       — the CORRECT sort position (1-based). Reordering (source
//                   language only) recomputes it so it stays unique + contiguous
//                   1..N. `index` is never a raw input.
//   • id_internal — stable identity, preserved untouched.
//
// variant "zone-detect" items:
//   { idInternal?, explanation, is_true_response, font_size, scale,
//     rotation_x, rotation_y, rotation_z, position_x?, position_y?, position_z? }
//   • explanation      — translatable text (editable in every language)
//   • is_true_response — marks which hotspots are real errors (structural flag,
//                        source language only). ~4% of live rows LACK idInternal,
//                        so rows are keyed by ARRAY POSITION, never by idInternal.
//
// The array is written straight back to react-hook-form so the existing save
// path round-trips it (content PUT + PLD sync).

export interface HotspotInfoFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (reorder, is_true_response) are source-language only. */
  isSourceLanguage?: boolean;
  /** Optional per-explanation character cap. */
  maxLength?: number;
  /** Drives which item shape + rules apply. */
  variant: "zone-sort" | "zone-detect";
}

// A hotspot item. All keys are optional/passthrough except the ones this editor
// mutates; unknown keys are preserved via spread on write.
interface HotspotItem {
  explanation?: string;
  index?: number;
  id_internal?: number;
  idInternal?: number;
  is_true_response?: boolean;
  [key: string]: unknown;
}

export default function HotspotInfoField({
  control,
  setValue,
  label,
  disabled = false,
  isSourceLanguage = true,
  maxLength,
  variant,
}: HotspotInfoFieldProps) {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "hotspot_info" });

  // RHF is the single source of truth. Derive display rows from it. For
  // zone-sort, present in ascending `index` order (top = position 1); zone-detect
  // keeps natural array order. Either way, the array position after this sort is
  // the canonical display position used by all handlers.
  const rows: HotspotItem[] = useMemo(() => {
    const arr: HotspotItem[] = Array.isArray(watched) ? watched : [];
    if (variant !== "zone-sort") return arr;
    return [...arr].sort((a, b) => {
      const ai = typeof a?.index === "number" ? a.index : 0;
      const bi = typeof b?.index === "number" ? b.index : 0;
      return ai - bi;
    });
  }, [watched, variant]);

  // Text (explanation) is editable in any language; structural controls
  // (reorder, is_true_response) are locked outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: HotspotItem[]) =>
    setValue("hotspot_info", next, { shouldDirty: true });

  // Reorder commit (zone-sort only): after moving a row, recompute every row's
  // `index` from its visual position so values stay UNIQUE + CONTIGUOUS 1..N.
  // Every other key on each item (id_internal, explanation, placement, font/scale)
  // is preserved untouched.
  const commitOrder = (nextRows: HotspotItem[]) => {
    write(nextRows.map((r, i) => ({ ...r, index: i + 1 })));
  };

  const handleExplanationChange = (position: number, value: string) => {
    write(rows.map((r, i) => (i === position ? { ...r, explanation: value } : r)));
  };

  const handleToggleTrueResponse = (position: number, checked: boolean) => {
    write(rows.map((r, i) => (i === position ? { ...r, is_true_response: checked } : r)));
  };

  const handleMoveUp = (position: number) => {
    if (position <= 0) return;
    const next = [...rows];
    [next[position - 1], next[position]] = [next[position], next[position - 1]];
    commitOrder(next);
  };

  const handleMoveDown = (position: number) => {
    if (position >= rows.length - 1) return;
    const next = [...rows];
    [next[position + 1], next[position]] = [next[position], next[position + 1]];
    commitOrder(next);
  };

  const isSort = variant === "zone-sort";
  const isDetect = variant === "zone-detect";

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      </div>

      {structureLocked && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {isSort && !structureLocked && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.hotspotReorderHint")}
        </p>
      )}

      <div className="space-y-3">
        {rows.map((row, position) => {
          const rowLabel = t("pages.project.editNode.hotspotItemLabel", {
            n: position + 1,
            defaultValue: `Hotspot ${position + 1}`,
          });
          const canMoveUp = isSort && !structureLocked && position > 0;
          const canMoveDown = isSort && !structureLocked && position < rows.length - 1;

          return (
            <div key={isSort ? (row.id_internal ?? position) : position} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-3">
                {isSort && (
                  <>
                    {/* Correct-position badge — reflects the row's order. */}
                    <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-sm font-semibold">
                      {position + 1}
                    </span>
                    <div className="shrink-0 flex flex-col">
                      <button
                        type="button"
                        onClick={() => handleMoveUp(position)}
                        disabled={!canMoveUp}
                        className="text-gray-500 p-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label={
                          t("general.moveUp", { defaultValue: "Move up" }) as string
                        }
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(position)}
                        disabled={!canMoveDown}
                        className="text-gray-500 p-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label={
                          t("general.moveDown", { defaultValue: "Move down" }) as string
                        }
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}

                <div className="flex-1 min-w-0">
                  <Input
                    type="text"
                    label={rowLabel}
                    value={typeof row.explanation === "string" ? row.explanation : ""}
                    onChange={(e) => handleExplanationChange(position, e.target.value)}
                    disabled={disabled}
                    maxLength={maxLength}
                    className="dynamic-field"
                  />
                </div>
              </div>

              {isDetect && (
                <div className="mt-1">
                  <Tooltip
                    message={
                      structureLocked
                        ? (t("pages.project.editNode.quizzStructureLockedHint") as string)
                        : ""
                    }
                  >
                    <Checkbox
                      label={
                        t("pages.project.editNode.hotspotIsTrueResponse", {
                          defaultValue: "This hotspot is a real error",
                        }) as string
                      }
                      checked={row.is_true_response === true}
                      disabled={structureLocked}
                      onChange={(e) => handleToggleTrueResponse(position, e.target.checked)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
